// BL-177 Gate 17d — Action Pill Standard v1. The one component for every action pill or action
// button that opens a submenu. Do not hand-roll a flyout; use this.
//
// Look: each option is its own pill (the therideatl.com contact launcher), stacked 8px apart.
// Timing: the same dwell, grace period and cascade as the Communications status-icon menu — both
// read src/lib/uiMotion.js. Behavior: a click on the parent pill keeps the parent's own action;
// hover (250ms), Enter/ArrowDown, or a touch long-press opens the menu; arrows move, Escape and a
// click outside close.
// Gate 17e (v1.01): only one menu is open at a time app-wide (src/lib/menuCoordinator.js), the
// menu fades out over 100ms when it closes, and the parent pill holds one steady look while open.
// Standard: Docs/Build_Execution/Platform/IntelAgent_Action_Pill_Standard_v1_01.md
//
// Options: [{ key, label, onSelect, icon?, iconClassName?, badge?: { text, className? },
//             disabled?, active?, title?, keepOpen?, displayOnly?, href?, options?: [...nested], placement?, mobilePlacement? }]

import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { actionPillCssVars } from './actionPillTokens';
import { claimOpenMenu } from '../../lib/menuCoordinator';
import {
    cascadeDelayMs,
    LONG_PRESS_MS,
    MENU_CLOSE_DWELL_MS,
    MENU_CLOSE_FADE_MS,
    MENU_ITEM_RISE_EASING,
    MENU_ITEM_RISE_MS,
    MENU_OPEN_DWELL_MS,
} from '../../lib/uiMotion';


// The id of the menu an option lives in, so a nested menu can tell the coordinator its parent.
const ParentMenuContext = createContext(null);

const OPEN_KEYS = {
    parent: ['Enter', 'ArrowDown'],
    right: ['Enter', 'ArrowRight'],
    left: ['Enter', 'ArrowLeft'],
};
const BACK_KEY = { right: 'ArrowLeft', left: 'ArrowRight' };

// Review fix round 1 (1.12.212-beta): a side submenu keeps this gap from the screen edge, and never gets narrower than this.
const SUBMENU_EDGE_GUTTER = 8;
const SUBMENU_MIN_WIDTH = 120;

// The same touch check the hover-open path has always used.
const isTouchDevice = () => typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

function ActionPillMenu({
    options = [],
    menuLabel = 'Actions',
    children,
    triggerClassName = '',
    triggerLabel,
    triggerTitle,
    triggerProps = {},
    triggerVariant = 'parent',
    onTriggerClick,
    disabled = false,
    menuDisabled = false,
    placement = 'bottom',
    mobilePlacement,
    align = 'start',
    size = 'default',
    open: openProp,
    onOpenChange,
    className = '',
    wrapperProps = {},
    menuClassName = '',
    scrollable = false,
    emptyLabel = '',
    onItemChosen,
    initialFocusKey,
}) {
    const menuId = `ia-pill-${useId().replace(/:/g, '')}`;
    const parentMenu = useContext(ParentMenuContext);
    const parentMenuId = parentMenu?.id;
    const controlled = openProp !== undefined;
    const [openState, setOpenState] = useState(false);
    const [revealed, setRevealed] = useState(false);
    const wrapperRef = useRef(null);
    const preferredPlacement = useCallback(() => {
        if (!mobilePlacement) return placement;
        const containerWidth = parentMenuId ? 0 : wrapperRef.current?.parentElement?.getBoundingClientRect?.().width || 0;
        const narrow = typeof window !== 'undefined' && (window.innerWidth < 768 || (containerWidth > 0 && containerWidth < 520));
        return narrow ? mobilePlacement : placement;
    }, [mobilePlacement, placement, parentMenuId]);
    const [responsivePlacement, setResponsivePlacement] = useState(preferredPlacement);
    const triggerRef = useRef(null);
    const rootTriggerRef = parentMenu?.rootTriggerRef || triggerRef;
    const rootPlacement = parentMenu?.rootPlacement || responsivePlacement;
    // The side the parent submenu opened on (null when the parent is the top-level column).
    const inheritedSide = parentMenu?.side || null;
    const menuRef = useRef(null);
    const dwellTimerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const longPressFiredRef = useRef(false);
    const focusOnOpenRef = useRef(false);
    const instantCloseRef = useRef(false);
    const lastPointerTypeRef = useRef(null);
    const menuAvailable = !menuDisabled && !disabled;
    const open = Boolean((controlled ? openProp : openState) && menuAvailable && parentMenu?.open !== false);
    const openRef = useRef(open);
    openRef.current = open;
    // Closing fades the whole menu out at once (MENU_CLOSE_FADE_MS) — unless another menu opening
    // forced it shut, which is instant. Derived during render so the fading menu never unmounts
    // for a frame first.
    const [closing, setClosing] = useState(false);
    const [prevOpen, setPrevOpen] = useState(open);
    if (prevOpen !== open) {
        setPrevOpen(open);
        setClosing(!open && !instantCloseRef.current);
    }
    const rendered = open || closing;
    const menuSpaceRef = useRef(null);
    const nested = triggerVariant === 'option';
    const side = placement === 'left' || placement === 'right' ? placement : null;
    const openKeys = side
        ? [...OPEN_KEYS.parent, ...OPEN_KEYS[side]]
        : placement === 'right' ? [...OPEN_KEYS.parent, 'ArrowRight'] : OPEN_KEYS.parent;
    const nearestIsLast = rootPlacement === 'top';

    const clearTimer = (ref) => {
        if (!ref.current) return;
        clearTimeout(ref.current);
        ref.current = null;
    };

    const setOpen = useCallback((next) => {
        if (!controlled) setOpenState(next);
        onOpenChange?.(next);
    }, [controlled, onOpenChange]);

    useEffect(()=>{if(parentMenu?.open===false)setOpen(false);},[parentMenu?.open,setOpen]);
    const closeMenu = useCallback(({ refocus = false } = {}) => {
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        if (openRef.current) setOpen(false);
        if (refocus) triggerRef.current?.focus();
    }, [setOpen]);

    const openWithFocus = () => {
        if (!menuAvailable) return;
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        focusOnOpenRef.current = true;
        if (openRef.current) focusNearest();
        else setOpen(true);
    };

    const ownItems = () => Array.from(menuRef.current?.querySelectorAll(`[data-ia-pill-item="${menuId}"]:not(:disabled)`) || []);

    // Keyboard open lands on `initialFocusKey` when that option is enabled, otherwise on the
    // option nearest the parent pill.
    const focusNearest = () => {
        const items = ownItems();
        const preferred = initialFocusKey === undefined || initialFocusKey === null
            ? null
            : items.find((item) => item.getAttribute('data-ia-pill-key') === String(initialFocusKey));
        (preferred || (nearestIsLast ? items[items.length - 1] : items[0]))?.focus();
    };

    useEffect(() => {
        if (!open) {
            instantCloseRef.current = false;
            return undefined;
        }
        if (scrollable && nearestIsLast && menuRef.current) {
            menuRef.current.scrollTop = menuRef.current.scrollHeight;
        }
        if (focusOnOpenRef.current) {
            focusOnOpenRef.current = false;
            focusNearest();
        }
        const frame = requestAnimationFrame(() => setRevealed(true));
        return () => cancelAnimationFrame(frame);
        // focusNearest and the initial scroll only read refs and props.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!closing) return undefined;
        const timer = setTimeout(() => setClosing(false), MENU_CLOSE_FADE_MS);
        return () => clearTimeout(timer);
    }, [closing]);

    // Fully gone: the next open starts its cascade from hidden again.
    useEffect(() => {
        if (!rendered) setRevealed(false);
    }, [rendered]);

    // One menu open at a time, app-wide: opening this one shuts every other (except this menu's
    // own parents) instantly. Layout effect so the other menu is gone before the browser paints.
    const forceCloseRef = useRef(() => {});
    forceCloseRef.current = () => {
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        instantCloseRef.current = true;
        setClosing(false);
        if (openRef.current) setOpen(false);
    };
    useLayoutEffect(() => {
        if (!rendered) return undefined;
        return claimOpenMenu(menuId, { parentId: parentMenuId, close: () => forceCloseRef.current() });
    }, [rendered, menuId, parentMenuId]);

    useLayoutEffect(() => {
        if (!rendered) return undefined;
        const fitMenu = () => {
            const menu = menuRef.current;
            const trigger = triggerRef.current;
            if (!menu || !trigger) return;
            const gutter = 8;
            const viewportRight = document.documentElement.clientWidth || window.innerWidth;
            const listPane=trigger.closest('[data-thread-list-pane]');
            const listBounds=listPane?.getBoundingClientRect();
            const leftEdge=listBounds?Math.max(gutter,listBounds.left+24):gutter;
            const right=listBounds?Math.min(viewportRight,listBounds.right-16):viewportRight;
            const anchor = trigger.getBoundingClientRect();
            const rootHeight = rootTriggerRef.current?.getBoundingClientRect().height || 44;
            menu.style.setProperty('--ia-pill-height', `${Math.round(rootHeight * .6)}px`);
            menu.style.setProperty('--ia-pill-font-size', `${Math.max(9, (parseFloat(getComputedStyle(rootTriggerRef.current || trigger).fontSize) || 14) * .65)}px`);
            const composer = rootTriggerRef.current?.closest('[data-shared-message-composer]');
            const upward = rootPlacement === 'top';
            const requested = preferredPlacement();
            const nextPlacement = parentMenuId ? 'right' : composer ? 'top' : ['left','right'].includes(requested) ? 'bottom' : requested;
            if (responsivePlacement !== nextPlacement) { setResponsivePlacement(nextPlacement); return; }
            menu.style.maxWidth = `${Math.max(0, right - 2 * gutter)}px`;
            menu.style.maxHeight = `${Math.max(0, window.innerHeight - 2 * gutter)}px`;
            menu.style.transform = '';
            menu.dataset.scrollable = scrollable && menu.scrollHeight > window.innerHeight - 2 * gutter ? 'true' : 'false';
            const bounds = menu.getBoundingClientRect();
            const desiredTop = parentMenuId
                ? (upward ? anchor.bottom - bounds.height : anchor.top)
                : upward ? anchor.top - bounds.height : anchor.bottom;
            const desiredLeft = parentMenuId ? anchor.right : align === 'end' ? anchor.right - bounds.width : anchor.left;
            const x = Math.max(leftEdge, Math.min(desiredLeft, right - gutter - bounds.width));
            const bottomEdge=listBounds?Math.min(window.innerHeight-gutter,listBounds.bottom-24):window.innerHeight-gutter;
            const y = Math.max(gutter, Math.min(desiredTop, bottomEdge - bounds.height));
            menu.style.transform = `translate(${x - bounds.left}px, ${y - bounds.top}px)`;
            if(!parentMenuId && !composer) {
                const filters=trigger.closest('.communications-filter-row');
                const row=trigger.closest('[data-thread-row]');
                const element=filters?wrapperRef.current:row;
                const property=filters?'marginBottom':'paddingBottom';
                if(element) {
                    const state=menuSpaceRef.current || {element,property,marginTop:element.style[property]};
                    const baseBottom=row?row.getBoundingClientRect().bottom-(parseFloat(row.style.paddingBottom)||0):0;
                    const space=filters?Math.ceil(bounds.height)+8:Math.max(0,Math.ceil(anchor.bottom+bounds.height-baseBottom))+24;
                    if(element.style[property]!==`${space}px`){element.style[property]=`${space}px`;requestAnimationFrame(fitMenu);}
                    menuSpaceRef.current=state;
                    const scroll=row?trigger.closest('[data-thread-scroll]'):null;
                    const overflow=anchor.bottom+bounds.height-bottomEdge;
                    if(scroll && overflow>0) {const previous=scroll.scrollTop;scroll.scrollTop+=overflow;if(scroll.scrollTop!==previous)requestAnimationFrame(fitMenu);}
                }
            }
            // Reserve space above this control, so its attached flyout never covers formatting or a wrapped control row.
            if(composer) {
                const rootTrigger=rootTriggerRef.current;
                const toolbar=rootTrigger?.closest('[data-composer-toolbar]');
                let control=rootTrigger?.parentElement;
                while(control?.parentElement && control.parentElement!==toolbar)control=control.parentElement;
                if(toolbar && control?.parentElement===toolbar) {
                    const rootWrapper=rootTrigger.parentElement;
                    const tops=Array.from(rootWrapper.querySelectorAll('.ia-action-pill-menu')).map(node=>node.getBoundingClientRect().top);
                    const reserve=Math.max(0,Math.ceil(rootTrigger.getBoundingClientRect().top-Math.min(...tops)))+8;
                    const rootState=rootWrapper.__iaMenuSpace || {element:control,marginTop:control.style.marginTop};
                    rootWrapper.__iaMenuSpace=rootState;
                    const nextSpace=`${reserve}px`;
                    if(rootState.element.style.marginTop!==nextSpace){rootState.element.style.marginTop=nextSpace;requestAnimationFrame(fitMenu);}
                    if(!parentMenuId)menuSpaceRef.current=rootState;
                }
            }
        };
        fitMenu();
        window.addEventListener('resize', fitMenu);
        window.addEventListener('scroll', fitMenu, true);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fitMenu);
        if (menuRef.current) observer?.observe(menuRef.current);
        if (wrapperRef.current) observer?.observe(wrapperRef.current);
        if (wrapperRef.current?.parentElement) observer?.observe(wrapperRef.current.parentElement);
        return () => {
            window.removeEventListener('resize', fitMenu);
            window.removeEventListener('scroll', fitMenu, true);
            observer?.disconnect();
        };
    }, [rendered, placement, mobilePlacement, responsivePlacement, align, options.length, menuClassName, preferredPlacement, parentMenuId, inheritedSide, rootTriggerRef, rootPlacement, scrollable]);

    useLayoutEffect(() => {
        if(!rendered && !parentMenuId && menuSpaceRef.current) {
            const state=menuSpaceRef.current;state.element.style[state.property || 'marginTop']=state.marginTop;
            if(rootTriggerRef.current?.parentElement)delete rootTriggerRef.current.parentElement.__iaMenuSpace;
            menuSpaceRef.current=null;
        }
    },[rendered,parentMenuId,rootTriggerRef]);
    useEffect(()=>()=>{if(menuSpaceRef.current)menuSpaceRef.current.element.style[menuSpaceRef.current.property || 'marginTop']=menuSpaceRef.current.marginTop;},[]);

    useLayoutEffect(() => {
        if (!rendered) {
            setResponsivePlacement(preferredPlacement());
        }
    }, [rendered, placement, mobilePlacement, preferredPlacement]);

    useEffect(() => {
        const syncPlacement = () => setResponsivePlacement(preferredPlacement());
        window.addEventListener('resize', syncPlacement);
        return () => window.removeEventListener('resize', syncPlacement);
    }, [placement, mobilePlacement, preferredPlacement]);

    // Keyboard and long-press opens have no pointer-leave to close them, so a click or tap outside
    // (or Escape anywhere) closes the menu.
    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (event) => {
            if (!wrapperRef.current?.contains(event.target)) closeMenu();
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') closeMenu();
        };
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, closeMenu]);

    useEffect(() => () => {
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        clearTimer(longPressTimerRef);
    }, []);

    const handlePointerEnter = (event) => {
        wrapperProps.onMouseEnter?.(event);
        clearTimer(closeTimerRef);
        const touchDevice = isTouchDevice();
        // Locked hotfix: only the composer's Templates chain (the submenus inside the channel picker) opens on hover under a
        // click preference. Send ▾, Signature and the channel picker itself keep click-to-open.
        const templatesHover = Boolean(parentMenuId && wrapperRef.current?.closest('[data-message-type-select]'));
        if (!menuAvailable || touchDevice || openRef.current || dwellTimerRef.current) return;
        dwellTimerRef.current = setTimeout(() => {
            dwellTimerRef.current = null;
            triggerProps.onMenuOpen?.();
            setOpen(true);
        }, MENU_OPEN_DWELL_MS);
    };

    const handlePointerLeave = (event) => {
        wrapperProps.onMouseLeave?.(event);
        clearTimer(dwellTimerRef);
        // A touch tap fires a compatibility mouseleave right after it. On touch, a menu closes by a tap outside, Escape or a
        // choice, never by the pointer "leaving" (that timer collapsed the composer submenus before the next tap).
        if (isTouchDevice() || lastPointerTypeRef.current === 'touch') return;
        if (!openRef.current || closeTimerRef.current) return;
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            setOpen(false);
        }, MENU_CLOSE_DWELL_MS);
    };

    const handleWrapperPointerDown = (event) => {
        wrapperProps.onPointerDownCapture?.(event);
        lastPointerTypeRef.current = event.pointerType || null;
    };

    const handleWrapperBlur = (event) => {
        wrapperProps.onBlur?.(event);
        // Tabbing to another element closes the menu; focus dropping to nothing (null, or the
        // document itself) doesn't.
        const next = event.relatedTarget;
        if (next?.nodeType === 1 && !wrapperRef.current?.contains(next)) closeMenu();
    };

    const handleTriggerClick = (event) => {
        triggerProps.onClick?.(event);
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        if (onTriggerClick) {
            onTriggerClick(event);
            return;
        }
        if (!menuAvailable) return;
        setOpen(!openRef.current);
    };

    const handleTriggerKeyDown = (event) => {
        triggerProps.onKeyDown?.(event);
        if (!menuAvailable) return;
        if (openKeys.includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
            openWithFocus();
            return;
        }
        if (event.key === 'Escape' && openRef.current) {
            event.stopPropagation();
            closeMenu();
        }
    };

    const longPressHandlers = menuAvailable ? {
        onTouchStart: (event) => {
            triggerProps.onTouchStart?.(event);
            longPressFiredRef.current = false;
            clearTimer(longPressTimerRef);
            longPressTimerRef.current = setTimeout(() => {
                longPressTimerRef.current = null;
                longPressFiredRef.current = true;
                openWithFocus();
            }, LONG_PRESS_MS);
        },
        onTouchEnd: (event) => {
            clearTimer(longPressTimerRef);
            if (longPressFiredRef.current) {
                // The long-press opened the menu; cancelling the touchend stops the browser also
                // turning the press into a click on the parent pill.
                longPressFiredRef.current = false;
                event.preventDefault();
            }
        },
        onTouchMove: () => clearTimer(longPressTimerRef),
        onTouchCancel: () => {
            clearTimer(longPressTimerRef);
            longPressFiredRef.current = false;
        },
        onContextMenu: (event) => event.preventDefault(),
    } : {};

    // The side a submenu actually opened on (it may have flipped left for room); its "back" arrow points home.
    const openSide = responsivePlacement === 'left' || responsivePlacement === 'right' ? responsivePlacement : side;
    const handleMenuKeyDown = (event) => {
        const items = ownItems();
        const index = items.indexOf(document.activeElement);
        let next = null;
        if (event.key === 'ArrowDown') next = items[(index + 1) % items.length];
        if (event.key === 'ArrowUp') next = items[(index - 1 + items.length) % items.length];
        if (event.key === 'Home') next = items[0];
        if (event.key === 'End') next = items[items.length - 1];
        if (next) {
            event.preventDefault();
            event.stopPropagation();
            next.focus();
            return;
        }
        if (event.key === 'Escape' || (nested && openSide && event.key === BACK_KEY[openSide])) {
            event.preventDefault();
            event.stopPropagation();
            closeMenu({ refocus: true });
        }
    };

    const choose = (option, event) => {
        event.stopPropagation();
        if (option.disabled) return;
        option.onSelect?.(option, event);
        if (option.keepOpen) return;
        closeMenu();
        onItemChosen?.();
    };

    // Gate 17e: no hand-off any more. While the menu is open the parent pill holds one steady look
    // whatever the pointer does among the options (see the global.css rules), so nothing flickers.
    // The parent's own `hover:` utilities are set aside while open too, so the pointer crossing back
    // over the parent can't toggle its background either.
    const baseTriggerClassName = typeof triggerClassName === 'function'
        ? triggerClassName({ open })
        : triggerClassName;
    const resolvedTriggerClassName = open
        ? String(baseTriggerClassName || '').split(/\s+/).filter((cls) => !cls.startsWith('hover:')).join(' ')
        : baseTriggerClassName;
    const {
        onClick: _onClick, onKeyDown: _onKeyDown, onTouchStart: _onTouchStart,
        onMenuOpen: _onMenuOpen, style: triggerStyle = {}, ...restTriggerProps
    } = triggerProps;
    const {
        onMouseEnter: _enter, onMouseLeave: _leave, onBlur: _blur, onPointerDownCapture: _pointerDown, className: wrapperExtraCls = '',
        style: wrapperStyle = {}, ...restWrapperProps
    } = wrapperProps;

    const itemState = (option, index) => {
        const delay = revealed ? cascadeDelayMs(index, options.length, { nearestIsLast }) : 0;
        return {
            'data-ia-pill-item': menuId,
            'data-ia-pill-key': option.key,
            'data-revealed': revealed ? 'true' : 'false',
            'data-has-icon': option.icon ? 'true' : 'false',
            'data-active': option.active ? 'true' : undefined,
            // Fading out: drop out of the accessibility tree and the tab order at once.
            ...(open ? {} : { role: undefined, tabIndex: -1 }),
            style: {
                transitionDuration: `${MENU_ITEM_RISE_MS}ms`,
                transitionTimingFunction: MENU_ITEM_RISE_EASING,
                // The cascade delay is for the reveal only (opacity, transform — the first two
                // transitioned properties); a hover colour change never waits behind it.
                transitionDelay: `${delay}ms, ${delay}ms, 0ms`,
            },
        };
    };

    const optionContent = (option) => (
        <>
            {option.icon && (
                <span aria-hidden="true" className={`ia-action-pill-icon ${option.iconClassName || ''}`}>{option.icon}</span>
            )}
            <span className="ia-action-pill-label">{option.label}</span>
            {option.badge && (
                <span className={`ia-action-pill-badge ${option.badge.className || ''}`}>{option.badge.text}</span>
            )}
        </>
    );

    const renderOption = (option, index) => {
        // ia-hover-pulse-off: option pills carry their own ring, not a workspace-wide button pulse.
        const optionCls = `ia-action-pill-option ia-hover-pulse-off ${option.className || ''}`;
        if (option.displayOnly) {
            const { 'data-ia-pill-item': _item, 'data-ia-pill-key': _key, ...state } = itemState(option, index);
            return (
                <span key={option.key} className={`${optionCls} ia-action-pill-display`} title={option.title} {...state}>
                    {optionContent(option)}
                </span>
            );
        }
        if (Array.isArray(option.options)) {
            const state = itemState(option, index);
            return (
                <ActionPillMenu
                    key={option.key}
                    options={option.options}
                    menuLabel={option.label}
                    triggerVariant="option"
                    triggerClassName={optionCls}
                    triggerTitle={option.title}
                    triggerProps={{ role: 'menuitem', ...state, onClick: option.onSelect ? (event)=>option.onSelect(option,event) : undefined, onMenuOpen: option.onSelect ? ()=>option.onSelect(option) : undefined }}
                    disabled={option.disabled}
                    placement={option.placement || 'right'}
                    mobilePlacement={option.mobilePlacement}
                    size={size}
                    scrollable={option.scrollable}
                    wrapperProps={{ className: 'ia-action-pill-nested' }}
                    onItemChosen={() => {
                        closeMenu();
                        onItemChosen?.();
                    }}
                >
                    {optionContent(option)}
                </ActionPillMenu>
            );
        }
        const Item = option.href && !option.disabled ? 'a' : 'button';
        return (
            <Item
                key={option.key}
                {...(Item === 'a' ? { href: option.href } : { type: 'button', disabled: option.disabled })}
                role="menuitem"
                title={option.title}
                className={optionCls}
                onClick={(event) => choose(option, event)}
                {...itemState(option, index)}
            >
                {optionContent(option)}
            </Item>
        );
    };

    return (
        <div
            ref={wrapperRef}
            className={`ia-action-pill-launcher relative inline-flex ${rendered ? 'z-50' : ''} ${wrapperExtraCls} ${className}`}
            {...restWrapperProps}
            style={{
                ...wrapperStyle,
            }}
            onMouseEnter={handlePointerEnter}
            onMouseLeave={handlePointerLeave}
            onPointerDownCapture={handleWrapperPointerDown}
            onBlur={handleWrapperBlur}
        >
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                aria-label={triggerLabel}
                title={triggerTitle}
                {...restTriggerProps}
                {...(menuDisabled ? {} : { 'aria-haspopup': 'menu', 'aria-expanded': open, 'aria-controls': open ? menuId : undefined })}
                data-open={open ? 'true' : 'false'}
                className={`${nested ? '' : 'ia-action-pill-trigger'} ${resolvedTriggerClassName}`}
                style={{
                    ...triggerStyle,
                }}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
                {...longPressHandlers}
            >
                {children}
            </button>
            {rendered && (
                // The menu box (padding included) is the hover bridge: the gaps between option
                // pills and the space back to the parent are inside it, so the pointer never
                // "leaves" while it moves between options.
                <div
                    ref={menuRef}
                    {...(open
                        ? { id: menuId, role: 'menu', 'aria-label': menuLabel }
                        : { 'aria-hidden': 'true', inert: '' })}
                    className={`ia-action-pill-menu ${menuClassName}`}
                    data-placement={responsivePlacement}
                    data-direction={rootPlacement === 'top' ? 'up' : 'down'}
                    data-align={align}
                    data-size={size}
                    data-scrollable={scrollable ? 'true' : 'false'}
                    data-closing={open ? 'false' : 'true'}
                    style={{
                        ...actionPillCssVars(size),
                    }}
                    onKeyDown={handleMenuKeyDown}
                >
                    <ParentMenuContext.Provider value={{ id: menuId, open, rootTriggerRef, rootPlacement, side: parentMenuId && (responsivePlacement === 'left' || responsivePlacement === 'right') ? responsivePlacement : null }}>
                        {options.map(renderOption)}
                    </ParentMenuContext.Provider>
                    {options.length === 0 && emptyLabel && (
                        <span className="ia-action-pill-option ia-action-pill-empty" data-revealed="true">
                            <span className="ia-action-pill-label">{emptyLabel}</span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export default ActionPillMenu;
